import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, paymentService } from '../services';
import { generatePaymentInvoicePdf } from '../utils/paymentInvoicePdf';
import './Invoices.css';

const Invoices = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [generateFor, setGenerateFor] = useState(null);
  const [lineItems, setLineItems] = useState([{ description: 'Training Program', quantity: 1, unitPrice: 25000 }]);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!auth.isAuthenticated()) {
      navigate('/login');
      return;
    }
    fetchPayments();
  }, [navigate]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await paymentService.getAll();
      setPayments(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.error || err.message || 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const getStudentName = (p) =>
    p.student && typeof p.student === 'object' ? p.student.fullName : '—';
  const getStudentPhone = (p) =>
    p.student && typeof p.student === 'object' ? p.student.phone || p.student.email : '—';
  const getStudentEmail = (p) =>
    p.student && typeof p.student === 'object' ? p.student.email : undefined;
  const getPaymentMethod = (p) => {
    if (p.method === 'office') return 'Office';
    if (p.method === 'bank_transfer' || p.receiptPath) return 'Bank Transfer';
    if (p.method === 'mobile_money' || p.paymentProof) return 'Mobile Money';
    return '—';
  };

  const openGenerate = (payment) => {
    setGenerateFor(payment);
    setLineItems([{ description: 'Training Program', quantity: 1, unitPrice: 25000 }]);
  };

  const closeGenerate = () => setGenerateFor(null);

  const updateLineItem = (index, field, value) => {
    setLineItems((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, [field]: field === 'description' ? value : Number(value) || 0 } : item
      )
    );
  };

  const addLineItem = () => {
    setLineItems((prev) => [...prev, { description: '', quantity: 1, unitPrice: 0 }]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length <= 1) return;
    setLineItems((prev) => prev.filter((_, i) => i !== index));
  };

  const generatePDF = async () => {
    if (!generateFor) return;
    setGenerating(true);
    try {
      await generatePaymentInvoicePdf({
        studentName: getStudentName(generateFor),
        studentPhone: getStudentPhone(generateFor),
        studentEmail: getStudentEmail(generateFor),
        lineItems,
        paymentDate: generateFor.createdAt || new Date().toISOString(),
        paymentMethod: getPaymentMethod(generateFor),
        amountPaid: generateFor.amount || lineItems.reduce((s, i) => s + (i.quantity || 0) * (i.unitPrice || 0), 0),
        receiptId: generateFor._id,
      });
      closeGenerate();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to generate PDF');
    } finally {
      setGenerating(false);
    }
  };

  const uniqueByStudent = [];
  const seen = new Set();
  payments.forEach((p) => {
    const sid = p.student?._id || p.student;
    if (sid && !seen.has(sid)) {
      seen.add(sid);
      uniqueByStudent.push(p);
    }
  });

  return (
    <div className="invoices-page">
      <header className="invoices-header">
        <h1>Receipts</h1>
        <p className="invoices-subtitle">Generate receipts for students who have completed payment</p>
      </header>

      {error && (
        <div className="invoices-error" onClick={() => setError('')}>
          {error}
        </div>
      )}

      {loading ? (
        <div className="invoices-loading">Loading payments...</div>
      ) : (
        <div className="invoices-card">
          <h2>Paid Students</h2>
          {uniqueByStudent.length === 0 ? (
            <p className="invoices-empty">No paid students. Payments appear here after students complete registration payment.</p>
          ) : (
            <table className="invoices-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Contact</th>
                  <th>Payment Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {uniqueByStudent.map((p) => (
                  <tr key={p._id}>
                    <td>{getStudentName(p)}</td>
                    <td>{getStudentPhone(p)}</td>
                    <td>{p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '—'}</td>
                    <td>
                      <button type="button" className="btn-generate" onClick={() => openGenerate(p)}>
                        Generate Receipt
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {generateFor && (
        <div className="invoice-modal-overlay" onClick={closeGenerate}>
          <div className="invoice-modal" onClick={(e) => e.stopPropagation()}>
            <div className="invoice-modal-header">
              <h3>Generate Receipt</h3>
              <button type="button" className="invoice-modal-close" onClick={closeGenerate} aria-label="Close">
                ×
              </button>
            </div>
            <div className="invoice-modal-body">
              <div className="invoice-billto">
                <strong>Bill To:</strong>
                <p>{getStudentName(generateFor)}</p>
                <p>{getStudentPhone(generateFor) || '—'}</p>
              </div>
              <div className="invoice-line-items">
                <strong>Line items</strong>
                <table className="line-items-table">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Qty</th>
                      <th>Unit Price (Rwf)</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((item, i) => (
                      <tr key={i}>
                        <td>
                          <input
                            value={item.description}
                            onChange={(e) => updateLineItem(i, 'description', e.target.value)}
                            placeholder="Description"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            min={1}
                            value={item.quantity}
                            onChange={(e) => updateLineItem(i, 'quantity', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            min={0}
                            value={item.unitPrice || ''}
                            onChange={(e) => updateLineItem(i, 'unitPrice', e.target.value)}
                            placeholder="0"
                          />
                        </td>
                        <td>
                          <button type="button" className="btn-remove-line" onClick={() => removeLineItem(i)} title="Remove">
                            −
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button type="button" className="btn-add-line" onClick={addLineItem}>
                  + Add line
                </button>
              </div>
            </div>
            <div className="invoice-modal-footer">
              <button type="button" className="btn-cancel" onClick={closeGenerate}>
                Cancel
              </button>
              <button type="button" className="btn-generate-pdf" onClick={generatePDF} disabled={generating}>
                {generating ? 'Generating...' : 'Download receipt'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoices;
